/**
 * SRG production dialog domains and their per-business-unit metadata.
 *
 * Single source of truth for the hostname→BU mapping, consumed both by the
 * SSO login flow (`util/loginproviders.ts`, which picks the per-BU OIDC
 * client) and by the per-language navbar logo config in `psi.config.ts`.
 * Kept dependency-free on purpose: `psi.config.ts` imports it at module
 * evaluation time, so importing it from `util/loginproviders.ts` directly
 * would create an import cycle (loginproviders → util/util → psi.config).
 */

// SRG production OIDC clients registered per business unit. The login flow
// picks the right provider based on the current hostname so each BU uses its
// own client ID (and gets its own audit trail on account.srgssr.ch). Only
// production hostnames map here — staging hostnames (`testdialog*.ch`) and
// direct psi2.dev / psi1.dev access intentionally fall through to the `srgInt`
// provider in loginproviders.ts, which targets the integration environment
// (account-int.srgssr.ch). `uiLocale` is sent as the `ui_locales` OIDC param so
// the SRG SSO consent page renders in the BU's primary language.
// `loginWithProviderAsync` overrides this at runtime with
// `document.documentElement.lang` if set, so this is a fallback for cases where
// `<html lang>` is missing.
export const SRG_BU_LOGIN_BY_HOSTNAME: Record<string, { key: string; clientId: string; name: string; uiLocale: string }> = {
    'dialogue.rts.ch': { key: 'srgRts', clientId: '62b42068-d443-4f8c-af94-7d44ce24cb10', name: 'RTS', uiLocale: 'fr' },
    'dialog.srf.ch': { key: 'srgSrf', clientId: '0ff5875e-d05c-4c3d-8678-4eba383a03ed', name: 'SRF', uiLocale: 'de' },
    'dialogo.rsi.ch': { key: 'srgRsi', clientId: '16522ab7-6779-4862-a6a7-f73b0aa86132', name: 'RSI', uiLocale: 'it' },
    'dialog.rtr.ch': { key: 'srgRtr', clientId: 'abd2df0c-d1ac-4a45-bcc1-f81c51c0af98', name: 'RTR', uiLocale: 'rm' },
    'dialogue.swissinfo.ch': { key: 'srgSwi', clientId: 'deeb5717-9412-4420-8b18-e91ceafdffdf', name: 'SWI', uiLocale: 'en' },
};

/**
 * UI locale of each SRG production dialog domain, derived from the per-BU
 * login mapping above so the hostname→language relationship has a single
 * source of truth. Used by the srg silo config to pick the per-language
 * navbar logo.
 */
export const SRG_UI_LOCALE_BY_HOSTNAME: Record<string, string> = Object.fromEntries(
    Object.entries(SRG_BU_LOGIN_BY_HOSTNAME).map(([hostname, bu]) => [hostname, bu.uiLocale]),
);
